import { useState } from 'react';
import { SalesDoc } from '../types';
import { format } from 'date-fns';
import { downloadSalesDocPdf } from '../utils/downloadPdf';
import { useCompanyProfile } from '@/features/recruitment/api';
import { resolveSalesDocUrl } from '../constants';

interface PrintableDocumentProps {
  doc: SalesDoc;
}

export function PrintableDocument({ doc }: PrintableDocumentProps) {
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const companyProfile = useCompanyProfile().data;

  const coName = companyProfile?.legalName || companyProfile?.name || '';
  const coAddr = companyProfile?.address || '';
  const coGstin = companyProfile?.gstin || companyProfile?.gst || '';
  const coPan = companyProfile?.pan || '';
  const coPhone = companyProfile?.phone || '';
  const coEmail = companyProfile?.officialEmail || companyProfile?.email || '';
  const coWeb = companyProfile?.website || '';
  const logoSrc = companyProfile?.logoUrl || null;

  const docTitle = doc?.title || 'Document';
  const qno = doc?.reference_number || '';
  const qdate = doc?.created_at ? format(new Date(doc.created_at), 'd MMM yyyy') : '';
  const qvalid = doc?.valid_until ? format(new Date(doc.valid_until), 'd MMM yyyy') : '';
  const prepBy = doc?.creator_name || '—';

  const handlePrintPdf = async () => {
    try {
      setDownloadingPdf(true);
      await downloadSalesDocPdf(doc);
    } catch (err) {
      console.error('Failed to download PDF:', err);
      window.print();
    } finally {
      setDownloadingPdf(false);
    }
  };

  const fileUrl = resolveSalesDocUrl(doc?.url);
  const isPdf = (url: string) => url.toLowerCase().endsWith('.pdf');
  const isImage = (url: string) => /\.(png|jpg|jpeg|gif|webp)$/i.test(url);
  const fileName = doc?.url ? doc.url.split('/').pop()?.split('?')[0] || 'Document' : 'No file';

  return (
    <div className="dt-simple-doc" id={`print-doc-${doc?.id || 'new'}`}>
      <style dangerouslySetInnerHTML={{ __html: `
        .dt-simple-doc {
          --accent: #1e3a8a;
          --accent-soft: #1e3a8a14;
          --ink: #111827;
          --muted: #6b7280;
          --line: #d8dbe2;
          --radius: 6px;
          font-family: 'Segoe UI', Inter, Arial, Helvetica, sans-serif;
          font-size: 12px;
          line-height: 1.55;
          color: var(--ink);
          background: #eceef1;
          padding: 24px 12px 60px;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .dt-simple-doc * { box-sizing: border-box; }
        .dt-simple-doc .toolbar {
          max-width: 210mm;
          margin: 0 auto 18px;
          background: #fff;
          border: 1px solid var(--line);
          border-radius: 10px;
          padding: 12px 14px;
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          align-items: center;
          box-shadow: 0 2px 10px rgba(0,0,0,.06);
          position: sticky;
          top: 8px;
          z-index: 50;
        }
        .dt-simple-doc .toolbar .grp {
          display: flex;
          gap: 6px;
          align-items: center;
          padding-right: 10px;
          border-right: 1px solid var(--line);
        }
        .dt-simple-doc .toolbar .grp:last-child { border-right: 0; }
        .dt-simple-doc .toolbar label { font-size: 11px; color: var(--muted); font-weight: 600; }
        .dt-simple-doc .toolbar button {
          font: inherit;
          font-size: 11.5px;
          padding: 6px 10px;
          border: 1px solid var(--line);
          background: #fff;
          border-radius: var(--radius);
          cursor: pointer;
          color: var(--ink);
        }
        .dt-simple-doc .toolbar button:hover { background: var(--accent-soft); border-color: var(--accent); }
        .dt-simple-doc .toolbar button.primary {
          background: var(--accent);
          color: #fff;
          border-color: var(--accent);
          font-weight: 600;
        }
        .dt-simple-doc .toolbar button.primary:hover { filter: brightness(1.12); }
        .dt-simple-doc .hint {
          max-width: 210mm;
          margin: 0 auto 14px;
          font-size: 11.5px;
          color: #4b5563;
          text-align: center;
        }
        .dt-simple-doc .sheet {
          width: 210mm;
          min-height: 297mm;
          margin: 0 auto;
          background: #fff;
          padding: 14mm 14mm 12mm;
          box-shadow: 0 4px 22px rgba(0,0,0,.12);
        }
        .dt-simple-doc .hdr {
          display: flex;
          justify-content: space-between;
          gap: 18px;
          align-items: flex-start;
          border-bottom: 3px solid var(--accent);
          padding-bottom: 12px;
        }
        .dt-simple-doc .brand { display: flex; gap: 12px; align-items: flex-start; }
        .dt-simple-doc .logo-slot {
          width: 74px; height: 74px; flex: none;
          border: 1px dashed var(--line);
          border-radius: var(--radius);
          display: flex; align-items: center; justify-content: center;
          overflow: hidden; background: #fafbfc;
          text-align: center; font-size: 9px; color: #9aa0aa; line-height: 1.25; padding: 4px;
        }
        .dt-simple-doc .logo-slot img { width: 100%; height: 100%; object-fit: contain; }
        .dt-simple-doc .co-name { font-size: 20px; font-weight: 700; color: var(--accent); letter-spacing: .2px; }
        .dt-simple-doc .co-tag { font-size: 10.5px; color: var(--muted); margin-bottom: 4px; }
        .dt-simple-doc .co-meta { font-size: 10.5px; color: #444; line-height: 1.6; }
        .dt-simple-doc .doc { text-align: right; flex: none; }
        .dt-simple-doc .doc h1 { font-size: 22px; letter-spacing: 2.5px; text-transform: uppercase; color: var(--accent); font-weight: 700; margin: 0; }
        .dt-simple-doc .doc .sub { font-size: 10px; color: var(--muted); letter-spacing: 1.2px; text-transform: uppercase; margin-top: 2px; }
        .dt-simple-doc .info { display: flex; gap: 10px; margin: 14px 0 12px; }
        .dt-simple-doc .box { border: 1px solid var(--line); border-radius: var(--radius); padding: 9px 11px; flex: 1; min-width: 0; }
        .dt-simple-doc .box.acct { background: var(--accent-soft); border-color: #c8d3ee; }
        .dt-simple-doc .box h3 { font-size: 9.5px; text-transform: uppercase; letter-spacing: .8px; color: var(--accent); font-weight: 700; margin: 0 0 5px 0; }
        .dt-simple-doc .box .nm { font-weight: 700; font-size: 12.5px; }
        .dt-simple-doc .box .ln { font-size: 10.5px; color: #4b5563; line-height: 1.6; }
        .dt-simple-doc .kv { display: flex; justify-content: space-between; gap: 8px; font-size: 10.5px; padding: 1.5px 0; }
        .dt-simple-doc .kv span:first-child { color: var(--muted); }
        .dt-simple-doc .kv span:last-child { font-weight: 600; text-align: right; }
        .dt-simple-doc .file-box { border: 1px solid var(--line); border-radius: var(--radius); padding: 12px; background: #fafbfc; margin-top: 12px; }
        .dt-simple-doc .file-box h3 { font-size: 9.5px; text-transform: uppercase; letter-spacing: .8px; color: var(--accent); font-weight: 700; margin: 0 0 8px 0; }
        .dt-simple-doc .file-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
        .dt-simple-doc .file-icon { width: 48px; height: 48px; border-radius: var(--radius); background: var(--accent-soft); display: flex; align-items: center; justify-content: center; color: var(--accent); flex-shrink: 0; }
        .dt-simple-doc .file-info { flex: 1; min-width: 150px; }
        .dt-simple-doc .file-name { font-weight: 600; font-size: 13px; word-break: break-all; }
        .dt-simple-doc .file-meta { font-size: 10.5px; color: var(--muted); margin-top: 2px; }
        .dt-simple-doc .file-link { text-decoration: none; color: var(--accent); font-weight: 500; }
        .dt-simple-doc .file-link:hover { text-decoration: underline; }
        .dt-simple-doc .preview { margin-top: 16px; border: 1px solid var(--line); border-radius: var(--radius); overflow: hidden; }
        .dt-simple-doc .preview img { width: 100%; height: auto; display: block; background: #f5f5f5; }
        .dt-simple-doc .pagefoot { margin-top: 16px; padding-top: 8px; border-top: 1px solid var(--line); display: flex; justify-content: space-between; gap: 12px; font-size: 9.5px; color: var(--muted); }
        @media print {
          body { background: #fff !important; padding: 0 !important; }
          .dt-simple-doc .toolbar, .dt-simple-doc .hint { display: none !important; }
          .dt-simple-doc .sheet { width: auto; min-height: 0; margin: 0; padding: 12mm 12mm 10mm; box-shadow: none; }
        }
      ` }} />

      {/* Toolbar */}
      <div className="toolbar">
        <div className="grp">
          <button type="button" className="primary" onClick={handlePrintPdf} disabled={downloadingPdf}>
            {downloadingPdf ? '⏳ Generating PDF...' : '🖨 Print / Save PDF'}
          </button>
        </div>
      </div>
      <div className="hint">Document preview — prints the document details and shows the uploaded file.</div>

      {/* Sheet */}
      <div className="sheet" id={`sheet-${doc?.id || 'new'}`}>
        {/* Header */}
        <div className="hdr">
          <div className="brand">
            <div className="logo-slot">
              {logoSrc ? <img src={logoSrc} alt="Logo" /> : <span>ADD<br />LOGO</span>}
            </div>
            <div>
              <div className="co-name">{coName}</div>
              <div className="co-meta">
                <div>{coAddr}</div>
                <div>GSTIN: <b>{coGstin}</b> &nbsp;·&nbsp; PAN: <b>{coPan}</b></div>
                <div><span>{coPhone}</span> &nbsp;·&nbsp; <span>{coEmail}</span> &nbsp;·&nbsp; <span>{coWeb}</span></div>
              </div>
            </div>
          </div>
          <div className="doc">
            <h1>{docTitle}</h1>
            <div className="sub">Document</div>
          </div>
        </div>

        {/* Info */}
        <div className="info">
          <div className="box">
            <h3>Document Details</h3>
            <div className="ln">
              <div>Reference: <b>{qno || '—'}</b></div>
              <div>Date: <b>{qdate}</b></div>
              <div>Valid Until: <b>{qvalid || '—'}</b></div>
              <div>Status: <b>{doc?.status || 'draft'}</b></div>
              <div>Folder: <b>{doc?.folder || '—'}</b></div>
              <div>Uploaded by: <b>{prepBy}</b></div>
            </div>
          </div>
          <div className="box acct">
            <h3>Company</h3>
            <div className="ln">
              <div>{coName}</div>
              <div>{coAddr}</div>
              <div>GSTIN: <b>{coGstin}</b></div>
              <div>{coPhone} &nbsp;·&nbsp; {coEmail}</div>
            </div>
          </div>
        </div>

        {/* Description */}
        {doc?.description && (
          <div className="file-box" style={{ marginTop: 16 }}>
            <h3>Description</h3>
            <div style={{ whiteSpace: 'pre-wrap', fontSize: '11px', lineHeight: 1.6 }}>{doc.description}</div>
          </div>
        )}

        {/* File - Main focus */}
        <div className="file-box" style={{ marginTop: 16 }}>
          <h3>Uploaded Document</h3>
          <div className="file-row">
            <div className="file-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            </div>
            <div className="file-info">
              <div className="file-name">{fileName}</div>
              <div className="file-meta">
                <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="file-link">
                  {doc?.url}
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Preview for PDF/Images - Full size */}
        {(isPdf(fileUrl || '') || isImage(fileUrl || '')) && (
          <div className="preview">
            {isPdf(fileUrl || '') ? (
              <iframe src={fileUrl} title="Document Preview" style={{ width: '100%', height: '600px', border: 'none' }} />
            ) : (
              <img src={fileUrl} alt="Document Preview" style={{ maxHeight: '70vh' }} />
            )}
          </div>
        )}

        {/* Page footer */}
        <div className="pagefoot">
          <span>{coName} · {coWeb} · {coEmail}</span>
          <span>This is a computer-generated document.</span>
        </div>
      </div>
    </div>
  );
}

export default PrintableDocument;